import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Box, CircularProgress, Stack, Typography, useTheme } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { ActionsMenu } from '@/components/table/actions'
import { ActionMergeQuarantine } from '@/components/table/actions/quarantine/ActionMergeQuarantine'
import { FilterableTable, TableBase } from '@/components/table/base'
import { usePlayers } from '@/stores/players'
import {
  STANDARIZED_GOLF_PLAYERS_TABLE_COLUMNS,
  STANDARIZED_GOLF_PLAYERS_TABLE_FILTERS
} from '@/views/DataAdmin/standarizedGolfPlayer.table'
import { StandarizedGolfPlayer } from '@/views/DataAdmin/types'

type QuarantineModalProps = {
  quarantinePlayer: StandarizedGolfPlayer
  open: boolean
  setModalOpen: (isOpen: boolean) => void
  onSubmit?: () => void
}

const QuarantineModal = ({
  quarantinePlayer,
  open,
  setModalOpen,
  onSubmit
}: QuarantineModalProps) => {
  const [loading, setLoading] = useState(false)
  const theme = useTheme()
  const {
    similarPlayers,
    isLoading,
    isFetching,
    fetchData,
    resetState,
    pagination,
    setPagination,
    filters,
    setFilters
  } = usePlayers(
    useShallow((state) => ({
      similarPlayers: state.data,
      isLoading: state.status === 'loading',
      isFetching: state.status === 'fetching',
      fetchData: state.fetchData,
      resetState: state.resetState,
      pagination: state.pagination,
      setPagination: state.setPagination,
      filters: state.filters,
      setFilters: state.setFilters
    }))
  )

  useEffect(() => {
    setFilters({
      firstName: [{ label: quarantinePlayer.firstName, value: quarantinePlayer.firstName.trim() }],
      lastName: [{ label: quarantinePlayer.lastName, value: quarantinePlayer.lastName.trim() }]
    })
    fetchData()
    return resetState
  }, [])

  const handleCloseAndReset = () => {
    setFilters({})
    setModalOpen(false)
  }

  const handleSubmit = () => {
    setFilters({})
    onSubmit?.()
  }

  return (
    <ConsoleDialog
      open={open}
      onClose={() => handleCloseAndReset()}
      title="Merge from quarantine"
      dialogSize={false}
      width={1200}
      smallTopMargin
      PaperProps={{
        sx: {
          position: 'relative'
        }
      }}
    >
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor={({ palette: { mode } }) =>
            mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.2)'
          }
          zIndex={1}
        >
          <CircularProgress />
        </Box>
      )}

      <Stack direction="column" gap={4}>
        <Stack gap={2}>
          <Typography fontWeight={700} variant="h6">
            Quarantine Player
          </Typography>
          <TableBase
            dense
            data={[quarantinePlayer]}
            columns={STANDARIZED_GOLF_PLAYERS_TABLE_COLUMNS}
            disablePagination
            disableColumnVisibility
          />
        </Stack>

        <Stack
          direction="column"
          sx={{
            p: 2,
            pb: 0,
            borderRadius: Number(theme.shape.borderRadius) / 2,
            position: 'relative',
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette.background.paper
                : theme.palette.grey[800]
          }}
        >
          <Typography fontWeight={700} variant="h6">
            Players
          </Typography>
          <FilterableTable
            dense
            pagination={pagination}
            onPaginationChange={setPagination}
            loading={isLoading}
            fetching={isFetching}
            rowActions={
              <ActionsMenu>
                <ActionMergeQuarantine
                  quarantinePlayer={quarantinePlayer}
                  setLoading={setLoading}
                  onSubmit={handleSubmit}
                />
              </ActionsMenu>
            }
            selectedFilters={filters}
            data={similarPlayers}
            onFiltersChange={setFilters}
            columns={STANDARIZED_GOLF_PLAYERS_TABLE_COLUMNS}
            filters={STANDARIZED_GOLF_PLAYERS_TABLE_FILTERS}
            disableColumnVisibility
          />
        </Stack>
      </Stack>
    </ConsoleDialog>
  )
}

export default QuarantineModal
